<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames, omit } from '@components/utils';

import {
  makeAlignProps,
  makeGroupProps,
  makeIconProps,
  makeSlideGroupProps,
  makeTagProps,
  makeThemeProps,
  provideTheme,
  useProxyModel,
  useRender
} from '../../composable';
import { CdsSlideGroup } from '../CdsSlideGroup';

import { CdsTabsV2Symbol } from './shared';
import { parseItems } from './utils';

import { CdsTabV2 } from './';

import type { TCdsTabItem } from './utils';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { PropType } from 'vue';

export const componentName = 'CdsTabsV2';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Список элементов
     */
    items: {
      type: Array as PropType<readonly TCdsTabItem[]>,
      default: () => []
    },
    /**
     * Занимать все доступное пространство
     */
    grow: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Задавать высоту всех элементов по самому высокому элементу
     */
    stacked: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    ...makeTagProps(),
    ...makeAlignProps(),
    ...makeSlideGroupProps(),
    ...makeThemeProps(),
    ...makeGroupProps({
      required: 'force' as const
    }),
    ...makeIconProps({
      prependIcon: 'chevron-left' as TCdsIconsName,
      appendIcon: 'chevron-right' as TCdsIconsName
    })
  },
  emits: {
    'update:modelValue': (value: unknown) => true
  },
  setup(props, { slots }) {
    const model = useProxyModel(props, 'modelValue');
    const parsedItems = computed(() => parseItems(props.items));
    const { themeClasses } = provideTheme(props);

    useRender(() => {
      const slideGroupProps = omit(props, ['align', 'grow', 'stacked', 'items']);

      return h(
        CdsSlideGroup,
        {
          ...slideGroupProps,
          modelValue: unref(model),
          role: 'tablist',
          class: classNames(
            'cds-tabs-v2',
            `cds-tabs-v2--align-${props.align}`,
            {
              'cds-tabs-v2--grow': props.grow,
              'cds-tabs-v2--stacked': props.stacked
            },
            unref(themeClasses)
          ),
          symbol: CdsTabsV2Symbol,
          'onUpdate:modelValue': (value: unknown) => {
            model.value = value;
          }
        },
        {
          default: () =>
            slots.default
              ? slots.default?.()
              : unref(parsedItems).map((item) => h(CdsTabV2, { ...item, key: item.title }))
        }
      );
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsTabsV2';
</style>
